import React from 'react';
import {
  Create,
  Edit,
  EditButton,
  Filter,
  List,
  maxLength,
  minLength,
  ReferenceField,
  ReferenceInput,
  required,
  SearchInput,
  TextField,
  TextInput,
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import ReferenceChild from '../../components/ReferenceChild';
import { getZ3PConfigValue, perPage } from '../../helper';

const ServiceOfferingDescriptionFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <ReferenceInput
      source="service_offering_id"
      reference="service-offering"
      sort={{ field: 'short_name', order: 'ASC' }}
      perPage={perPage}
      alwaysOn
    >
      <ReferenceChild
        {...props}
        optionText="short_name"
        source="service_offering_id"
      />
    </ReferenceInput>
  </Filter>
);

const isUsingHelpText = getZ3PConfigValue('is_using_help_text');

export const ServiceOfferingDescriptionList = ({ classes, ...props }) => (
  <div>
    <List
      {...props}
      filters={<ServiceOfferingDescriptionFilter />}
      bulkActionButtons={<BulkDeleteButton />}
      sort={{ field: 'service_offering_id', order: 'ASC' }}
      perPage={50}
    >
      <ZippityDatagrid>
        <TextField source="created_at" />
        <TextField source="modified_at" />
        <TextField source="service_offering_description_id" />
        <ReferenceField
          source="service_offering_id"
          reference="service-offering"
          sortable={false}
        >
          <TextField source="short_name" />
        </ReferenceField>
        <TextField source="subheader" />
        <TextField source="text" />

        <EditButton />
      </ZippityDatagrid>
    </List>

    {isUsingHelpText && (
      <div>
        <strong>Service Descriptions</strong> are displayed as{' '}
        <a href="/screenshots/service-description.png" target="_blank">
          modals (popups) in the booking app
        </a>
        .
        <ul>
          <li>They give extra information about each service.</li>
          <li>The modal appears if the customer clicks &quot;Details&quot;.</li>
          <li>
            Each one has a <strong>Subheader</strong> and a longer Text entry.
          </li>
          <li>
            If there&apos;s more than one for a service, they render in order of
            &quot;Description Id&quot;.
          </li>
          <li>
            If you need to change the order, right now the only way to do so is
            to delete the old description items for that service, and then
            create new ones in the correct order.
          </li>
        </ul>
      </div>
    )}
  </div>
);

const editStyles = {
  service_offering_id: { display: 'inline-block' },
};

const validateSubheader = [required(), minLength(2), maxLength(255)];
const validateText = [required(), minLength(2), maxLength(1023)];

export const ServiceOfferingDescriptionEdit = withStyles(editStyles)(
  ({ classes, ...props }) => (
    <Edit undoable={false} {...props}>
      <ZippitySimpleForm>
        <ReferenceInput
          source="service_offering_id"
          reference="service-offering"
          sort={{ field: 'short_name', order: 'ASC' }}
          perPage={perPage}
          validate={[required()]}
        >
          <ReferenceChild
            {...props}
            optionText="short_name"
            source="service_offering_id"
          />
        </ReferenceInput>
        <TextInput multiline validate={validateSubheader} source="subheader" />
        <TextInput multiline validate={validateText} source="text" />
      </ZippitySimpleForm>
    </Edit>
  ),
);

export const ServiceOfferingDescriptionCreate = withStyles(editStyles)(
  ({ classes, ...props }) => (
    <Create {...props}>
      <ZippitySimpleForm>
        <ReferenceInput
          source="service_offering_id"
          reference="service-offering"
          sort={{ field: 'short_name', order: 'ASC' }}
          perPage={perPage}
          validate={[required()]}
        >
          <ReferenceChild
            {...props}
            optionText="short_name"
            source="service_offering_id"
          />
        </ReferenceInput>
        <TextInput multiline validate={validateSubheader} source="subheader" />
        <TextInput multiline validate={validateText} source="text" />
      </ZippitySimpleForm>
    </Create>
  ),
);
